import { computed } from 'vue';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { Client, Entity, FinancialYear, FinancialYearOverview, Practice, User } from '@repo/core/models';
import { useSessionStorage } from '@vueuse/core';
import {
  useClientStore,
  useEntityStore,
  useFinancialStore,
  usePracticeStore,
  useTabStore,
  useUserStore
} from '@/stores';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@repo/vue/stores';

export const useRootStore = defineStore('root', () => {
  // utils
  const route = useRoute();

  // stores
  const authStore = useAuthStore();
  const practiceStore = usePracticeStore();
  const clientStore = useClientStore();
  const entityStore = useEntityStore();
  const userStore = useUserStore();
  const financialStore = useFinancialStore();
  const tabStore = useTabStore();

  // state
  const practiceId = useSessionStorage<string>('practiceId', null);
  const clientId = useSessionStorage<string>('clientId', null);
  const supportAccessRequestId = useSessionStorage<string>('supportAccessRequestId', null);
  const financialYearId = useSessionStorage<string>('financialYearId', null);
  const entityId = useSessionStorage<string>('entityId', null);

  // getters
  const userProfileId = computed(() => authStore.user?.profile?.sub);
  const page = computed(() => route.name?.toString());
  const tabId = computed(() => route.query.tabId?.toString());
  const isClientSelected = computed(() => !!clientStore.currentClient?.id);
  const isClientReadonly = computed(() => clientStore.isReadOnly);

  const setPracticeId = (data: string | undefined) => {
    practiceId.value = data;
  };
  const setClientId = (data: string | undefined) => {
    clientId.value = data;
  };
  const setEntityId = (data: string | undefined) => {
    entityId.value = data;
  };
  const setCurrentPractice = (data: Practice) => {
    practiceStore.currentPractice = data;
  };
  const setCurrentClient = (data: Client) => {
    clientStore.currentClient = data;
  };
  const setCurrentEntity = (data: Entity) => {
    entityStore.currentEntity = data;
  };
  const setCurrentUser = (data: User) => {
    userStore.currentUser = data;
  };
  const clearCurrentPractice = () => {
    practiceId.value = null;
    practiceStore.currentPractice = <Practice>{};
  };
  const clearCurrentClient = () => {
    clientId.value = null;
    clientStore.currentClient = <Client>{};
  };
  const clearCurrentEntity = () => {
    entityId.value = null;
    entityStore.currentEntity = <Entity>{};
  };
  const clearCurrentUser = () => {
    userStore.currentUser = <User>{};
  };
  const clearCurrentTicket = () => {
    supportAccessRequestId.value = null;
  };
  const clearCurrentYear = () => {
    // financialYearId is non-destructive in eventStore.setContext (an omitted header
    // never clobbers it), so it must be cleared explicitly here - otherwise the prior
    // client's year lingers as the FinancialYearId request header and setDefaultPeriods
    // skips selecting the new client's year (it bails when financialYearId is set).
    financialYearId.value = null;
    financialStore.selectedPeriod = {} as FinancialYear;
    financialStore.financialYearOverview = {} as FinancialYearOverview;
  };
  const clearCurrentTabs = () => {
    tabStore.resetTabState();
  };
  // Clear every tenancy claim the root store owns. Used on logout so nothing leaks into the next
  // user on the same tab (their practiceId/clientId would otherwise be sent as request headers
  // and /users/getCurrent would 204). Mirrors the legacy Angular app's localStorageService.clearAll()
  // on logout. See DC-5864.
  const clearAll = () => {
    practiceId.value = undefined;
    clientId.value = undefined;
    entityId.value = undefined;
    financialYearId.value = undefined;
    supportAccessRequestId.value = undefined;
  };

  const state = {
    userProfileId,
    clientId,
    practiceId,
    supportAccessRequestId,
    financialYearId,
    entityId
  };
  const getters = {
    page,
    tabId,
    isClientSelected,
    isClientReadonly
  };
  const actions = {
    setPracticeId,
    setClientId,
    setEntityId,
    setCurrentPractice,
    setCurrentClient,
    setCurrentEntity,
    setCurrentUser,
    clearCurrentPractice,
    clearCurrentClient,
    clearCurrentEntity,
    clearCurrentUser,
    clearCurrentTicket,
    clearCurrentYear,
    clearCurrentTabs,
    clearAll
  };

  return { ...state, ...getters, ...actions };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRootStore, import.meta.hot));
}
